var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import axios, { AxiosPromise } from 'axios';
import CookieUtils from '../framework/utils/CookieUtils';
import appConfig from '../../config/app.config';
import { LOCAL_STORAGE_KEY, HTTP_TO_MANY_REQUESTS, SERVICE_UNAVAILABLE, isCloudflareChallenge } from '../framework/constants/app';
import { authenticatedRequest } from './firebase/authenticated';
import { claimChallengeRedirect, getChallengeRedirectTarget } from './cfChallengeRedirect';
class BaseHttp {
    constructor(config) {
        Object.defineProperty(this, "client", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: axios.create()
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                headers: {},
                baseURL: '',
                ignoreHeaders: false
            }
        });
        Object.defineProperty(this, "_getHeaders", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (headers) => {
                if (this.config.ignoreHeaders) {
                    return {};
                }
                // general headers for every API call
                const headersObj = {
                    'autode-user-cookie-id': CookieUtils.resolveCookieId(),
                    'autode-user-device': appConfig.headers['autode-user-device'],
                    'autode-user-lang': localStorage.getItem(LOCAL_STORAGE_KEY.i18n_KEY) || 'de'
                };
                // ADV-263: resolveSessionId() re-sets its cookie's TTL on every call (sliding 30-min
                // window), so simply reading it on every request — same as cookie-id above — is what
                // makes the session extend through continuous browsing rather than needing an explicit
                // "renew" call from a route-change hook. Crawlers get none: the header is left out rather
                // than sent as "undefined", which axios would pass on as one shared fake session id.
                const sessionId = CookieUtils.resolveSessionId();
                if (sessionId)
                    headersObj['autode-user-session-id'] = sessionId;
                // ADV-263: consent state read fresh on every request (not cached) — the accept/reject
                // modal writes localStorage synchronously, so a decision made mid-visit is reflected on
                // the very next call rather than needing a page reload.
                const consent = CookieUtils.getConsentState();
                headersObj['autode-user-consent-marketing'] = String(consent.marketingConsent);
                headersObj['autode-user-consent-analytics'] = String(consent.analyticsConsent);
                // if authorized user add userId header to general headers
                if (CookieUtils.resolveUserID()) {
                    headersObj['autode-user-id'] = CookieUtils.resolveUserID();
                }
                return Object.assign({}, BaseHttp.headers, this.config.headers, headersObj, headers);
            }
        });
        Object.defineProperty(this, "_getURL", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (url) => {
                return `${this.config.baseURL}/${url}`;
            }
        });
        Object.defineProperty(this, "_processRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (url, method, data, headers) => {
                const requestHeaders = this._getHeaders(headers);
                const requestUrl = this._getURL(url);
                const requestConfig = {
                    method,
                    url: requestUrl,
                    headers: requestHeaders
                };
                // place all data in get params
                // if we use GET request type
                if (method === 'get') {
                    requestConfig.params = data;
                }
                else {
                    requestConfig.data = data;
                }
                return this.client(requestConfig)
                    .then((response) => response.data)
                    .catch((error) => {
                    let errorObj = {};
                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        if (error.response.status === HTTP_TO_MANY_REQUESTS) {
                            errorObj = error.response;
                        }
                        else {
                            errorObj = error.response.data;
                        }
                        if (error.response.status === SERVICE_UNAVAILABLE || isCloudflareChallenge(error.response)) {
                            // window.location.replace() is a top-level navigation: XHR cannot run a Cloudflare
                            // challenge, a page can. It is limited to one attempt per cooldown per tab (see
                            // claimChallengeRedirect); a repeat failure is left to reject normally so the calling
                            // block renders its own empty/graceful state instead of reloading again.
                            const target = getChallengeRedirectTarget(error.response.status, error.request && error.request.responseURL, window.location);
                            if (target !== null && claimChallengeRedirect()) {
                                window.location.replace(`${appConfig.redirectUrl.serviceUnavailable}${target}`);
                            }
                        }
                    }
                    else if (error.request) {
                        // The request was made but no response was received
                        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                        // http.ClientRequest in node.js
                        errorObj = error.request;
                    }
                    else {
                        // Something happened in setting up the request that triggered an Error
                        errorObj = error.message;
                    }
                    throw errorObj;
                });
            }
        });
        if (config) {
            this.setConfig(config);
        }
    }
    setConfig(config) {
        this.config = Object.assign({}, this.config, config);
    }
    /**
     * Method for regular request without authentication.
     * If you add beforeRequest(), it must return PROMISE!
     * */
    request(method, url, data, headers) {
        if (this.config.beforeRequest) {
            return this.config.beforeRequest()
                .then(() => this._processRequest(url, method, data, headers));
        }
        return this._processRequest(url, method, data, headers);
    }
    /**
     * Some requests need JWT token.
     * This method add Authorization header.
     * If you add beforeRequest(), it must be return PROMISE!
     * */
    privateRequest(method, url, data, headers) {
        return this.request(method, url, data, headers);
    }
}
Object.defineProperty(BaseHttp, "headers", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: {
        Accept: 'application/json'
    }
});
Object.defineProperty(BaseHttp, "setHeaders", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: (headers) => {
        BaseHttp.headers = Object.assign({}, BaseHttp.headers, headers);
    }
});
__decorate([
    authenticatedRequest,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String, Object, Object]),
    __metadata("design:returntype", typeof (_a = typeof AxiosPromise !== "undefined" && AxiosPromise) === "function" ? _a : Object)
], BaseHttp.prototype, "privateRequest", null);
export default BaseHttp;
